import { React } from 'Imports';
import { useRef, useState } from 'react';
import { isEncompass } from '$Utilities/integrationPlatform';

interface TimeInputProps {
    onChange?: (hour?: number, minute?: number, second?: number, amPm?: 'AM' | 'PM') => void;
    initialValue?: { hour: number; minute: number; second: number; amPm: 'AM' | 'PM' };
}

const styles = require('./TimeInput.scss') as {
    container: string;
    inputGeotab: string;
    inputEncompass: string;
    toggleButtonContainerGeotab: string;
    toggleButtonContainerEncompass: string;
    toggleButton: string;
    toggleButtonActive: string;
};

export const TimeInput = ({ onChange, initialValue }: TimeInputProps) => {
    const [hourStr, setHourStr] = useState(initialValue ? String(initialValue.hour).padStart(2, '0') : '');
    const [minuteStr, setMinuteStr] = useState(initialValue ? String(initialValue.minute).padStart(2, '0') : '');
    const [secondStr, setSecondStr] = useState(initialValue ? String(initialValue.second).padStart(2, '0') : '');
    const [amPm, setAmPm] = useState<'AM' | 'PM'>(initialValue?.amPm ?? 'AM');

    const hourRef = useRef<HTMLInputElement>(null);
    const minuteRef = useRef<HTMLInputElement>(null);
    const secondRef = useRef<HTMLInputElement>(null);

    const inputClass = isEncompass ? styles.inputEncompass : styles.inputGeotab;
    const toggleContainerClass = isEncompass ? styles.toggleButtonContainerEncompass : styles.toggleButtonContainerGeotab;

    const notify = (h: string, m: string, s: string, ap: 'AM' | 'PM') => {
        const hNum = parseInt(h, 10);
        const mNum = parseInt(m, 10);
        const sNum = parseInt(s, 10);
        const valid =
            h !== '' && !isNaN(hNum) && hNum >= 1 && hNum <= 12 &&
            m !== '' && !isNaN(mNum) && mNum >= 0 && mNum <= 59 &&
            s !== '' && !isNaN(sNum) && sNum >= 0 && sNum <= 59;
        onChange?.(
            valid ? hNum : undefined,
            valid ? mNum : undefined,
            valid ? sNum : undefined,
            valid ? ap : undefined,
        );
    };

    const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 2);

        if (raw.length === 2) {
            const num = parseInt(raw, 10);
            if (num < 1 || num > 12) {
                // 2-digit result is out of range — try keeping just the first digit
                const first = parseInt(raw[0], 10);
                const safe = first >= 1 && first <= 9 ? raw[0] : '';
                setHourStr(safe);
                if (safe) minuteRef.current?.focus();
                notify(safe, minuteStr, secondStr, amPm);
                return;
            }
            setHourStr(raw);
            minuteRef.current?.focus();
            notify(raw, minuteStr, secondStr, amPm);
            return;
        }

        setHourStr(raw);
        // Single digit 2–9 can't start a valid 2-digit hour (13–19 are all invalid)
        if (raw.length === 1 && parseInt(raw, 10) >= 2) {
            minuteRef.current?.focus();
        }
        notify(raw, minuteStr, secondStr, amPm);
    };

    const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 2);

        if (raw.length === 2) {
            const num = parseInt(raw, 10);
            if (num > 59) {
                setMinuteStr('');
                notify(hourStr, '', secondStr, amPm);
                return;
            }
            setMinuteStr(raw);
            secondRef.current?.focus();
            notify(hourStr, raw, secondStr, amPm);
            return;
        }

        setMinuteStr(raw);
        // Single digit 6–9 can't start a valid 2-digit minute (60–99 are all invalid)
        if (raw.length === 1 && parseInt(raw, 10) >= 6) {
            secondRef.current?.focus();
        }
        notify(hourStr, raw, secondStr, amPm);
    };

    const handleSecondChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 2);

        if (raw.length === 2 && parseInt(raw, 10) > 59) {
            setSecondStr('');
            notify(hourStr, minuteStr, '', amPm);
            return;
        }

        setSecondStr(raw);
        notify(hourStr, minuteStr, raw, amPm);
    };

    const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && minuteStr === '') {
            hourRef.current?.focus();
        }
    };

    const handleSecondKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && secondStr === '') {
            minuteRef.current?.focus();
        }
    };

    const handleAmPmChange = (value: 'AM' | 'PM') => {
        setAmPm(value);
        notify(hourStr, minuteStr, secondStr, value);
    };

    return (
        <div className={styles.container}>
            <input
                ref={hourRef}
                className={inputClass}
                placeholder="HH"
                value={hourStr}
                onChange={handleHourChange}
                maxLength={2}
                inputMode="numeric"
            />
            <span>:</span>
            <input
                ref={minuteRef}
                className={inputClass}
                placeholder="MM"
                value={minuteStr}
                onChange={handleMinuteChange}
                onKeyDown={handleMinuteKeyDown}
                maxLength={2}
                inputMode="numeric"
            />
            <span>:</span>
            <input
                ref={secondRef}
                className={inputClass}
                placeholder="SS"
                value={secondStr}
                onChange={handleSecondChange}
                onKeyDown={handleSecondKeyDown}
                maxLength={2}
                inputMode="numeric"
            />
            <div className={toggleContainerClass}>
                <button
                    type="button"
                    className={amPm === 'AM' ? styles.toggleButtonActive : styles.toggleButton}
                    onClick={() => handleAmPmChange('AM')}
                >
                    AM
                </button>
                <button
                    type="button"
                    className={amPm === 'PM' ? styles.toggleButtonActive : styles.toggleButton}
                    onClick={() => handleAmPmChange('PM')}
                >
                    PM
                </button>
            </div>
        </div>
    );
};
